'use client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  MdOutlineShoppingCart,
  MdOutlineNotifications,
  MdMenu,
  MdOutlineSearch,
  MdHistory,
  MdOutlineInfo,
  MdOutlineLogout,
  MdChatBubbleOutline,
  MdOutlinePersonOutline,
  MdStorefront,
  MdKeyboardArrowRight,
  MdOutlinePerson,
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
  MdOutlineFavoriteBorder,
  MdOutlineEvent,
  MdClose,
  MdOutlineRepeat,
  MdOutlinePersonAddAlt1,
  MdShoppingCartCheckout,
} from 'react-icons/md';
import { TiShoppingBag } from 'react-icons/ti';
import { VscTextSize } from 'react-icons/vsc';
import { FaRegSmile } from 'react-icons/fa';
import { RiCupLine, RiLeafLine } from 'react-icons/ri';
import { IoHandRightOutline } from 'react-icons/io5';
import { styled } from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import nthLogo from '../../../public/images/logo-s.svg';
import InputSearch from '../molecules/InputSearch';
import {
  BREAKPOINT_PC,
  BREAKPOINT_PC_MIDDLE_NUMBER,
  BREAKPOINT_SP,
  zIndexMap,
} from '@/util/const';
import { TbCoins } from 'react-icons/tb';
import { GrCircleQuestion } from 'react-icons/gr';
import PullDownFrame from '../molecules/PullDownFrame';
import { RiBook2Line, RiCoupon3Line } from 'react-icons/ri';
import { signOut, useSession } from 'next-auth/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import RadioGroup from '../molecules/RadioGroup';
import { LiaBoxSolid } from 'react-icons/lia';
import useWidth from '@/util/useWidth';
import ContentText from '../atoms/ContentText';
import Button from '../atoms/Button';
import Header from '../atoms/Header';
import defaultIcon from '@/../../public/images/noUserImageIcon.svg';
import NotificationBadge from '../molecules/NotificationBadge';
import { cart_productAttributes } from '@/models/cart_product';

import { useCart } from '../provider/SessionProvider';
import { watchCartProductCount } from '@/util/firebase/client/cart';
import { watchChat } from '@/util/firebase/client/chat';
import { watchNotification } from '@/util/firebase/client/notification';
import { Unsubscribe } from 'firebase/firestore';

type Props = {
  couponCount: number;
  setFontsizeCookies: (size: 's' | 'm' | 'l') => void;
  fontSize: 's' | 'm' | 'l';
};
const iconSize = 24;

const SiteHeader: React.FC<Props> = ({
  couponCount,
  setFontsizeCookies,
  fontSize,
}) => {
  const { data: session, update } = useSession();
  const router = useRouter();
  const [keyword, setKeyword] = useState('');
  const { isMobile, width } = useWidth();
  const [searchIcon, setSearchIcon] = useState(<MdOutlineSearch size={20} />);
  const [startSpsearch, setStartSpsearch] = useState(false);
  const [spsearchDetail, setSpsearchDetail] = useState(false);
  const [spsearchCategory, setSpsearchCategory] = useState(false);
  const [notifCount, setNotifCount] = useState<number | null>(null);
  const [cartCount, setCartCount] = useState<number | null>(null);
  const [chatCount, setChatCount] = useState<number | null>(null);
  const user = useMemo(
    () => ({
      name: session?.user.name,
      isAgent: session?.user.isAgent,
      coupons: couponCount,
      image: session?.user.image,
    }),
    [session, couponCount],
  );
  const isLogin = !!session?.user;
  const containerRef = useRef(null);

  useEffect(() => {
    // vh単位の実際の値を計算
    const updateVhProperty = () => {
      // 1vhの実際のピクセル値を計算
      const vh = window.innerHeight * 0.01;
      // カスタムプロパティを設定
      document.documentElement.style.setProperty('--real-vh', `${vh}px`);
    };

    updateVhProperty(); // 初期設定

    // イベントリスナー
    window.addEventListener('resize', updateVhProperty);
    window.addEventListener('scroll', updateVhProperty);
    window.addEventListener('orientationchange', updateVhProperty);

    return () => {
      window.removeEventListener('resize', updateVhProperty);
      window.removeEventListener('scroll', updateVhProperty);
      window.removeEventListener('orientationchange', updateVhProperty);
    };
  }, []);

  const onClickLink = (path: string) => {
    window.location.href = '/' + path;
  };
  const pathname = usePathname();
  // const searchParams = useSearchParams();

  const menuList = useMemo(() => {
    const temp = [];
    isLogin &&
      temp.push({
        label: 'マイページ・登録情報',
        icon: <MdOutlinePersonOutline size={16} />,
        color: 'var(--background-primary)',
        border: '1px solid var(--gray-300-border)',
        url: '/mypage',
      });
    temp.push({
      label: 'お知らせ・キャンペーン一覧',
      icon: <MdOutlineInfo size={16} />,
      color: 'var(--light-blue-200)',
      url: '/news',
    });
    isLogin &&
      temp.push({
        label: '購入履歴',
        icon: <MdHistory size={16} />,
        color: 'var(--lime-200)',
        url: '/mypage/orders',
      });
    isLogin &&
      temp.push({
        label: 'お気に入り商品',
        icon: <MdOutlineFavoriteBorder size={16} />,
        color: 'var(--yellow-200)',
        url: '/product?favorite=true',
      });
    process.env.NEXT_PUBLIC_EVENT_ENABLE == 'true' &&
      temp.push({
        label: 'イベント一覧',
        icon: <MdOutlineEvent size={16} />,
        color: 'var(--orange-200)',
        url: '/events',
      });
    temp.push({
      label: 'サロン一覧',
      icon: <MdStorefront size={16} />,
      color: 'var(--red-200-selected-accent)',
      url: '/salons',
    });
    // temp.push({
    //   label: 'FAQ',
    //   icon: <GrCircleQuestion size={16} />,
    //   color: 'var(--pink-200)',
    //   url: '/faq',
    // });
    temp.push({
      label: 'ライブラリ',
      icon: <RiBook2Line size={16} />,
      color: 'var(--purple-200)',
      url: '/library',
    });
    if (isLogin && session?.user.isAgent) {
      temp.push({
        label: '代理注文',
        icon: <MdShoppingCartCheckout size={16} />,
        color: 'var(--purple-200)',
        url: '/agent/assisted',
      });
    }
    isLogin &&
      (session?.user.contact?.is_salon_user__c
        ? temp.push(
            {
              label: '会員登録',
              icon: <MdOutlinePersonAddAlt1 size={16} />,
              color: 'var(--bg-tertiary-gray-100)',
              url: '/signup/1',
            },
            {
              label: 'サロン管理画面',
              icon: <MdStorefront size={16} />,
              color: 'var(--bg-tertiary-gray-100)',
              url: '/salons/admin',
            },
          )
        : temp.push({
            label: 'ログアウト',
            icon: <MdOutlineLogout size={16} />,
            color: 'var(--gray-300-border)',
            onClick: () => {
              signOut({
                callbackUrl: '/',
              });
            },
          }));
    return temp;
  }, [isLogin, session?.user.contact?.is_salon_user__c, session?.user.isAgent]);

  const searchList = useMemo(() => {
    const temp = [];
    temp.push({
      label: 'ヘナ',
      icon: <RiLeafLine size={15} />,
      color: 'var(--lime-200)',
      url: '/product?category=ヘナ',
    });
    temp.push({
      label: '化粧品',
      icon: <FaRegSmile size={15} />,
      color: 'var(--yellow-200)',
      url: '/product?category=化粧品',
    });
    temp.push({
      label: '健康食品',
      icon: <RiCupLine size={15} />,
      color: 'var(--orange-200)',
      url: '/product?category=健康食品',
    });
    temp.push({
      label: 'その他',
      icon: <IoHandRightOutline size={15} />,
      color: 'var(--pink-200)',
      url: '/product?category=その他',
    });
    // temp.push({
    //   label: '定期便',
    //   icon: <MdOutlineRepeat size={15} />,
    //   color: 'var(--purple-200)',
    //   url: '/product?category=継続購入',
    // });
    temp.push({
      label: '全ての商品',
      icon: <TiShoppingBag size={15} />,
      color: 'var(--light-blue-200)',
      url: '/product',
    });
    !spsearchCategory &&
      temp.push({
        label: 'キーワードで商品検索',
        icon: <MdOutlineSearch size={15} />,
        color: 'var(--gray-300-border)',
        onClick: () => {
          setSpsearchDetail(true);
        },
      });
    return temp;
  }, [spsearchCategory]);

  function handleSearchProduct() {
    if (keyword) {
      const url = new URL(window.location.href);
      url.searchParams.set('keyword', keyword);
      if (pathname === '/product') {
        url.searchParams.set('page', '1');
      } else {
        url.pathname = '/product';
        url.search = `?keyword=${encodeURIComponent(keyword)}`;
      }
      window.location.href = url.toString();

      // let params = `keyword=${keyword}`;
      // if (pathname == '/product' && searchParams) {
      //   const urlSearchParams = new URLSearchParams(searchParams.toString());
      //   urlSearchParams.set('keyword', keyword);
      //   urlSearchParams.set('page', '1');
      //   params = urlSearchParams.toString();
      // }
      // window.location.href = `/product?${params}`;
    }
  }

  const refObj = useRef<HTMLElement>(null);
  const el = refObj.current;
  const hundleClickOutside = useCallback(
    (e: MouseEvent) => {
      setStartSpsearch((o) => {
        if (o) {
          if (
            !el?.parentElement?.contains(e.target as Node) &&
            e.target != refObj.current?.parentElement?.parentElement
          ) {
            document.removeEventListener('click', hundleClickOutside);
            return false;
          }
        }
        return o;
      });
    },
    [refObj.current],
  );

  // useEffect(() => {
  //   // 通知非同期取得
  //   const interval = setInterval(
  //     (function temp() {
  //       fetchPromise('/api/notification/personal/get').then((res) => {
  //         setNotifCount(res.data.count ?? null);
  //       });
  //       return temp;
  //     })(),
  //     60000,
  //   ); // 1min

  //   return () => {
  //     clearInterval(interval);
  //   };
  // }, []);
  const { cartUpdatedFlag } = useCart();
  useEffect(() => {
    if (session?.user.contact?.is_salon_user__c) {
      const cartDataString = localStorage.getItem('cart');
      const cartData = cartDataString
        ? (JSON.parse(cartDataString) as cart_productAttributes[])
        : [];
      const totalQuantity = cartData.reduce(
        (sum, product) => sum + (product.quantity || 0),
        0,
      );
      if (totalQuantity > 0) setCartCount(totalQuantity);
      else setCartCount(null);
    }
  }, [session, cartUpdatedFlag, pathname]);
  useEffect(() => {
    const subFirebase = () => {
      const subs = [];
      if (session?.user.uid) {
        console.log(338, session);
        const sub1 = watchNotification(session?.user.uid, (notifications) => {
          setNotifCount(notifications.length);
        });
        subs.push(sub1);
        const sub2 = watchChat(session.user.uid, (v) => {
          setChatCount(v.map((it) => it.unread).reduce((a, b) => a + b, 0));
        });
        subs.push(sub2);
        if (!session?.user.contact?.is_salon_user__c) {
          const sub3 = watchCartProductCount(session?.user.uid, (v) => {
            setCartCount(v);
          });
          subs.push(sub3);
        }
      }
      return subs;
    };
    let allSubs: Promise<Unsubscribe>[] = [];
    if (session?.user.uid) {
      console.log(335, 'start sub');
      allSubs = subFirebase();
    }
    return () => {
      if (allSubs.length) {
        console.log(357, 'finish sub');
        Promise.all(allSubs);
      }
    };
  }, [session?.user.uid]);

  useEffect(() => {
    if (startSpsearch) {
      setTimeout(() => {
        document.addEventListener('click', hundleClickOutside);
      }, 400);
    }
    return () => {
      document.removeEventListener('click', hundleClickOutside);
    };
  }, [startSpsearch]);

  useEffect(() => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        // console.log('Page is now visible. Triggering session update...');
        update()
          .then((updatedSession) => {
            // console.log('Session updated successfully:', updatedSession);
          })
          .catch((error) => {
            console.error('Error updating session:', error);
          });
      }
    };
    window.addEventListener('visibilitychange', visibilityHandler, false);
    return () =>
      window.removeEventListener('visibilitychange', visibilityHandler, false);
  }, [update]);

  return (
    <Container ref={containerRef}>
      {spsearchDetail && (
        <div className="spsearchDetail">
          <div className="spsearchInput">
            <div className="spsearchInputSub">
              <MdChevronLeft
                size={32}
                onClick={() => {
                  setSpsearchDetail(false);
                  setSpsearchCategory(false);
                }}
              />
              <InputSearch
                className="input-height-small"
                label={''}
                value={keyword}
                onChange={(v: string) => {
                  setKeyword(v);
                  if (v.length != 0) {
                    setSearchIcon(
                      <Button
                        theme="icon"
                        label={
                          <MdClose
                            size={16}
                            onClick={() => {
                              setKeyword('');
                              setSearchIcon(<MdOutlineSearch size={16} />);
                            }}
                          />
                        }
                      ></Button>,
                    );
                  } else {
                    setSearchIcon(<MdOutlineSearch size={16} />);
                  }
                }}
                placeholder="商品を検索する"
                endIcon={searchIcon}
                onEnterPressed={handleSearchProduct}
                maxLength={80}
                widthLength={isMobile ? 150 : undefined}
              />
            </div>
            {!spsearchCategory && (
              <div
                className="spsearchInputSub"
                onClick={() => {
                  setSpsearchCategory(true);
                }}
              >
                <ContentText type="article-l" className="category">
                  カテゴリから検索
                </ContentText>
                <MdChevronRight size={32} />
              </div>
            )}
            {spsearchCategory && (
              <SpsearchCategoryWarpper>
                {searchList.map((elm) => (
                  <div
                    key={elm.label}
                    onClick={() => {
                      setSpsearchDetail(false);
                      setSpsearchCategory(false);
                    }}
                    className="row-container"
                  >
                    <span>
                      {elm.url ? (
                        <Link href={elm.url} className="search-single-row">
                          <span
                            className="search-elm-icon"
                            style={{ backgroundColor: elm.color }}
                          >
                            {elm.icon}
                          </span>
                          {elm.label}
                          <MdKeyboardArrowRight
                            size="24px"
                            color="var(--base-black, #18181B)"
                            style={{ marginLeft: 'auto' }}
                          ></MdKeyboardArrowRight>
                        </Link>
                      ) : (
                        <></>
                      )}
                    </span>

                    <hr
                      style={{
                        borderTop: '1px solid #cccccc',
                      }}
                    />
                  </div>
                ))}
              </SpsearchCategoryWarpper>
            )}
          </div>
          <div></div>
        </div>
      )}
      <div className={`container ${isLogin ? 'login' : ''}`}>
        <div
          style={{ order: isLogin ? 3 : 0 }}
          className={'header-search_box pc' + (isLogin ? ' flex-fill' : '')}
        >
          <InputSearch
            className="input-height"
            label={''}
            value={keyword}
            onChange={(v: string) => {
              setKeyword(v);
              if (v.length != 0) {
                setSearchIcon(
                  <Button
                    theme="icon"
                    label={
                      <MdClose
                        size={28}
                        onClick={() => {
                          setKeyword('');
                          setSearchIcon(<MdOutlineSearch size={28} />);
                        }}
                      />
                    }
                  ></Button>,
                );
              } else {
                setSearchIcon(<MdOutlineSearch size={28} />);
              }
            }}
            placeholder="商品を検索する"
            endIcon={searchIcon}
            onEnterPressed={handleSearchProduct}
            maxLength={80}
            widthLength={isMobile ? 150 : undefined}
          />
        </div>

        <div
          style={{ order: isLogin ? 0 : 3 }}
          className={'header-logo pc' + (isLogin ? '' : ' flex-fill')}
        >
          <Image
            src={nthLogo}
            width={45}
            height={45}
            alt="nth-logo"
            onClick={() => {
              onClickLink('');
            }}
          />
        </div>

        {isLogin && (
          <div style={{ order: '2' }} className="header-user_info pc">
            {user.isAgent && (
              <div className="header-user-image cursorPointer">
                {
                  <Image
                    src={user.image || defaultIcon.src}
                    width={36}
                    height={36}
                    alt="user-icon"
                    onClick={() => {
                      onClickLink('mypage');
                    }}
                  />
                }
              </div>
            )}
            {width > BREAKPOINT_PC_MIDDLE_NUMBER && (
              <div>
                <div
                  className="cursorPointer icon header-user-name"
                  onClick={() => {
                    onClickLink('mypage');
                  }}
                >
                  <span className="header-user-name-pc">{user.name}</span>様
                  <MdKeyboardArrowRight size="24px"></MdKeyboardArrowRight>
                </div>
                <div className="header-user-coupon">
                  <div className="user-coupon-icon-number">
                    <div
                      className="cursorPointer icon header-user-name"
                      onClick={() => {
                        onClickLink('mypage/coupon');
                      }}
                    >
                      <span>
                        <RiCoupon3Line color="var(--logo-gold)" />
                      </span>
                      <span>{user.coupons}枚</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div style={{ order: '4' }} className="header-buttons">
          <div className="header-button sp">
            <PullDownFrame
              target="#site-header"
              handler={(setIsOpen) => (
                <MdMenu
                  size={iconSize}
                  onClick={() => {
                    setIsOpen(true);
                  }}
                />
              )}
              pos={'left'}
            >
              {(setIsOpen, refObj) => {
                return (
                  <PullDownContainer ref={refObj}>
                    {isLogin ? (
                      <div className="header-user_info sp">
                        <div className="header-user-image cursorPointer">
                          {user.image && (
                            <Image
                              src={user.image}
                              width={36}
                              height={36}
                              alt="user-icon"
                              onClick={() => {
                                onClickLink('mypage');
                              }}
                            />
                          )}
                        </div>
                        <div>
                          <div
                            className="cursorPointer icon"
                            onClick={() => {
                              onClickLink('mypage');
                            }}
                          >
                            <div className="header-user-name">
                              <Header
                                type="h-xs-sp"
                                className="header-user-name-length"
                              >
                                {user.name}&nbsp;
                              </Header>
                              <Header type="h-xxs-sp" className="user-suffix">
                                様
                              </Header>

                              {/* <MdKeyboardArrowRight
                              size="24px"
                              color="var(--base-black, #18181B)"
                            ></MdKeyboardArrowRight> */}
                            </div>
                          </div>
                          <div className="header-user-coupon">
                            <div className="user-coupon-icon-number">
                              <div
                                className="cursorPointer icon"
                                onClick={() => {
                                  onClickLink('mypage/coupon');
                                }}
                              >
                                <span>
                                  <RiCoupon3Line color="var(--logo-gold)" />
                                </span>
                                <span>{user.coupons}枚</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="notlogin">
                        <ContentText>ログインしていません</ContentText>
                      </div>
                    )}
                    {menuList.map((elm) => (
                      <div
                        key={elm.label}
                        onClick={() => {
                          setIsOpen(false);
                        }}
                        className="row-container"
                      >
                        <span>
                          {elm.url ? (
                            <Link href={elm.url} className="menu-single-row">
                              <span
                                className="menu-elm-icon"
                                style={{
                                  backgroundColor: elm.color,
                                  border: elm.border,
                                }}
                              >
                                {elm.icon}
                              </span>
                              {elm.label}
                            </Link>
                          ) : (
                            <div
                              onClick={elm.onClick}
                              className="menu-single-row"
                            >
                              <span
                                className="menu-elm-icon"
                                style={{
                                  backgroundColor: elm.color,
                                  border: elm.border,
                                }}
                              >
                                {elm.icon}
                              </span>
                              {elm.label}
                            </div>
                          )}
                        </span>
                      </div>
                    ))}
                    <div
                      className="fontsize-radio-container"
                      style={{
                        background: 'white',
                        flexDirection: 'column',
                      }}
                      ref={refObj}
                    >
                      <div>文字サイズ変更</div>
                      <RadioGroup
                        className="fontsize-radio"
                        isRadioGroupButton={true}
                        defaultValue={fontSize}
                        options={[
                          {
                            label: '小',
                            value: 's',
                          },
                          {
                            label: '中',
                            value: 'm',
                          },
                          {
                            label: '大',
                            value: 'l',
                          },
                        ]}
                        onchange={(select) => {
                          setFontsizeCookies(select as 's' | 'm' | 'l');
                          setIsOpen(false);
                        }}
                      ></RadioGroup>
                    </div>
                    {!isLogin && (
                      <Link className="loginbutton" href={'/login'}>
                        <Button
                          label="会員ログイン"
                          onClick={() => {
                            setIsOpen(false);
                          }}
                        ></Button>
                      </Link>
                    )}
                  </PullDownContainer>
                );
              }}
            </PullDownFrame>
            <ContentText type="label-m" className="header-button-color">
              メニュー
            </ContentText>
          </div>
          <div
            className="header-button sp"
            onClick={() => {
              isMobile
                ? setStartSpsearch(!startSpsearch)
                : onClickLink('product');
            }}
          >
            <MdSearch size={iconSize} />
            <ContentText type="label-m" className="header-button-color">
              商品検索
            </ContentText>
          </div>
          {isMobile && (
            <StartSpsearchWarpper $isOpen={startSpsearch}>
              {searchList.map((elm) => (
                <div
                  key={elm.label}
                  onClick={() => {
                    setStartSpsearch(false);
                  }}
                  className="row-container"
                >
                  <span>
                    {elm.url ? (
                      <Link href={elm.url} className="search-single-row">
                        <span
                          className="search-elm-icon"
                          style={{ backgroundColor: elm.color }}
                        >
                          {elm.icon}
                        </span>
                        {elm.label}
                      </Link>
                    ) : (
                      <div onClick={elm.onClick} className="search-single-row">
                        <span
                          className="search-elm-icon"
                          style={{ backgroundColor: elm.color }}
                        >
                          {elm.icon}
                        </span>
                        {elm.label}
                      </div>
                    )}
                  </span>

                  <hr
                    style={{
                      borderTop:
                        elm.label == 'キーワードで商品検索'
                          ? 'none'
                          : '1px solid #cccccc',
                    }}
                  />
                </div>
              ))}
            </StartSpsearchWarpper>
          )}
          {isLogin && (
            <>
              <div
                className="header-button sp"
                onClick={() => {
                  onClickLink('news');
                }}
              >
                <NotificationBadge displayChar={notifCount}>
                  <MdOutlineNotifications size={iconSize} />
                </NotificationBadge>
                <ContentText type="label-m" className="header-button-color">
                  通知
                </ContentText>
              </div>
            </>
          )}
          <div
            className="header-button"
            onClick={() => {
              onClickLink('cart');
            }}
          >
            <NotificationBadge displayChar={cartCount}>
              <MdOutlineShoppingCart size={iconSize} />
            </NotificationBadge>
            <ContentText type="label-m" className="header-button-color">
              カート
            </ContentText>
          </div>
          <div
            className="header-button pc"
            onClick={() => onClickLink('product')}
          >
            <LiaBoxSolid size={iconSize} />
            <ContentText type="label-m" className="header-button-color">
              商品一覧
            </ContentText>
          </div>
          {isLogin && (
            <>
              {user.isAgent && (
                <div
                  className="header-button"
                  onClick={() => onClickLink('chatroom')}
                >
                  <NotificationBadge displayChar={chatCount}>
                    <MdChatBubbleOutline size={iconSize} />
                  </NotificationBadge>
                  <ContentText type="label-m" className="header-button-color">
                    チャット
                  </ContentText>
                </div>
              )}
              <div
                className="header-button pc"
                onClick={() => {
                  onClickLink('news');
                }}
              >
                <NotificationBadge displayChar={notifCount}>
                  <MdOutlineNotifications size={iconSize} />
                </NotificationBadge>
                <ContentText type="label-m" className="header-button-color">
                  通知
                </ContentText>
              </div>
            </>
          )}
          {!isLogin && (
            <div
              onClick={() => {
                onClickLink('login');
              }}
              className="header-button"
            >
              <MdOutlinePerson size={iconSize} />
              <ContentText type="label-m" className="header-button-color">
                ログイン
              </ContentText>
            </div>
          )}
          {isLogin && (
            <div
              onClick={() => {
                onClickLink('mypage');
              }}
              className="header-button sp"
            >
              <MdOutlinePersonOutline size={iconSize} />
              <ContentText type="label-m" className="header-button-color">
                マイページ
              </ContentText>
            </div>
          )}
          <div className="pc">
            <PullDownFrame
              target="#site-header"
              handler={(setIsOpen) => (
                <div
                  onClick={() => setIsOpen(true)}
                  className="header-button pc"
                >
                  <VscTextSize size={iconSize} />
                  <ContentText type="label-m" className="header-button-color">
                    サイズ
                  </ContentText>
                </div>
              )}
              pos={'right'}
            >
              {(setIsOpen, refObj) => {
                return (
                  <div
                    className="fontsize-radio-container"
                    style={{
                      background: 'white',
                      flexDirection: 'column',
                    }}
                    ref={refObj}
                  >
                    <div>文字サイズ変更</div>
                    <RadioGroup
                      className="fontsize-radio"
                      isRadioGroupButton={true}
                      defaultValue={fontSize}
                      options={[
                        {
                          label: '小',
                          value: 's',
                        },
                        {
                          label: '中',
                          value: 'm',
                        },
                        {
                          label: '大',
                          value: 'l',
                        },
                      ]}
                      onchange={(select) => {
                        setFontsizeCookies(select as 's' | 'm' | 'l');
                        setIsOpen(false);
                      }}
                    ></RadioGroup>
                  </div>
                );
              }}
            </PullDownFrame>
          </div>
          <div style={{ order: '99' }} className="header-menu pc">
            <PullDownFrame
              target="#site-header"
              handler={(setIsOpen) => (
                <MdMenu
                  size={32}
                  onClick={() => {
                    setIsOpen(true);
                  }}
                />
              )}
              pos={'right'}
            >
              {(setIsOpen, refObj) => {
                return (
                  <PullDownContainer ref={refObj}>
                    {menuList.map((elm) => (
                      <div
                        key={elm.label}
                        onClick={() => {
                          setIsOpen(false);
                        }}
                        className="row-container"
                      >
                        <span>
                          {elm.url ? (
                            <Link href={elm.url} className="menu-single-row">
                              <span
                                className="menu-elm-icon"
                                style={{
                                  backgroundColor: elm.color,
                                  border: elm.border,
                                }}
                              >
                                {elm.icon}
                              </span>
                              {elm.label}
                            </Link>
                          ) : (
                            <div
                              onClick={elm.onClick}
                              className="menu-single-row"
                            >
                              <span
                                className="menu-elm-icon"
                                style={{
                                  backgroundColor: elm.color,
                                  border: elm.border,
                                }}
                              >
                                {elm.icon}
                              </span>
                              {elm.label}
                            </div>
                          )}
                        </span>
                      </div>
                    ))}
                  </PullDownContainer>
                );
              }}
            </PullDownFrame>
          </div>
        </div>
      </div>
      <div id="site-header"></div>
    </Container>
  );
};

export default SiteHeader;

const SpsearchCategoryWarpper = styled.div`
  position: absolute;
  width: 100%;
  z-index: 1000;
  bottom: 0;
  height: calc(100% - 50px);
  background-color: white;
  justify-content: flex-start !important;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  transition: bottom 0.5s;

  hr {
    margin: 0;
    width: calc(100vw);
  }
  .row-container {
    background-color: white;
    :hover {
      background-color: var(--background-tertiary);
    }
    a {
      color: inherit;
      text-decoration: none;
    }
  }
  .search-single-row {
    height: 56px;
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 0 16px;
    gap: 8px;
    cursor: pointer;
    @media (max-width: ${BREAKPOINT_SP}) {
      height: 26px;
      padding: 8px 16px;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 25px;
      letter-spacing: 0.04em;
      font-family: 'Noto Sans JP';
      text-align: left;
    }
  }
  .search-elm-icon {
    padding: 8px;
    border-radius: 4px;
    pointer-events: none;
    @media (max-width: ${BREAKPOINT_SP}) {
      height: 18px;
      width: 18px;
      justify-self: center;
      display: flex;
      align-items: center;
      padding: 2px 2px 2px 4px;
      border-radius: 4px;
    }
  }
`;
const StartSpsearchWarpper = styled.div<{
  $isOpen: boolean;
}>`
  position: absolute;
  width: 100%;
  z-index: 1000;
  height: 310px !important;
  background-color: white;
  justify-content: flex-start !important;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  transition: transform 0.5s;
  bottom: 61px;
  transform: translateY(
    ${(props) => (props.$isOpen ? '0' : 'calc(100% + 61px)')}
  );

  hr {
    margin: 0;
    width: calc(100vw - 20px);
  }
  .row-container {
    background-color: white;
    :hover {
      background-color: var(--background-tertiary);
    }
    a {
      color: inherit;
      text-decoration: none;
    }
  }
  .search-single-row {
    height: 56px;
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 0 16px;
    gap: 8px;
    cursor: pointer;
    @media (max-width: ${BREAKPOINT_SP}) {
      height: 26px;
      padding: 8px;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 25px;
      letter-spacing: 0.04em;
      text-align: left;
      font-family: 'Noto Sans JP';
    }
  }
  .search-elm-icon {
    padding: 8px;
    border-radius: 4px;
    pointer-events: none;
    @media (max-width: ${BREAKPOINT_SP}) {
      height: 18px;
      width: 18px;
      justify-self: center;
      display: flex;
      align-items: center;
      padding: 2px 2px 2px 4px;
      border-radius: 4px;
    }
  }
`;
const Container = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: ${zIndexMap.header};
  @media (max-width: ${BREAKPOINT_SP}) {
    position: fixed;
    top: unset;
    bottom: 0;
    .spsearchDetail {
      width: 100vw;
      height: 100vh;
      height: calc(var(--real-vh, 1vh) * 100); /* 動的に計算された高さ */
      background-color: white;
      position: absolute;
      bottom: 0px;
      z-index: ${zIndexMap.headerPopup};
      gap: 12px;
      display: flex;
      flex-direction: column;
      .spsearchInput {
        gap: 0px;
        .spsearchInputSub {
          width: 100vw;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: 8px 16px;
          gap: 8px;
          box-sizing: border-box;
          border-bottom: solid 1px var(--gray-400-border, #a1a1aa);
          svg {
            flex-shrink: 0;
          }
          .category {
            font-family: 'Noto Sans JP';
            align-self: center;
          }
          .input-height-small {
            width: 100%;

            div {
              div {
                padding: 0px;
                gap: 8px;
                display: flex;
                flex-wrap: wrap;
                align-content: space-between;
                justify-content: space-between;
                input {
                  font-size: 14px;
                  line-height: 2.2;
                  padding: 0px 8px;
                  flex-shrink: 0;
                }
                svg {
                  height: 16px;
                  width: 16px;
                  margin-top: 4px;
                  box-sizing: content-box;
                  flex-shrink: 0;
                }
              }
            }
          }
        }
      }
    }
    .pc {
      display: none !important;
    }
  }
  @media (min-width: ${BREAKPOINT_PC}) {
    .sp {
      display: none !important;
    }
  }
  #site-header {
    position: relative;
    z-index: -1;
  }
  .notlogin {
    padding: 12px;
    background-color: var(--bg-tertiary-gray-100, #f4f4f5);
  }
  .loginbutton {
    box-sizing: border-box;
    padding: 16px;
    width: 100%;
    position: absolute;
    bottom: 68px;
  }

  .container {
    filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.2));
    display: flex;
    justify-content: space-around;
    gap: 12px;
    padding: 8px;
    box-sizing: border-box;
    background-color: white;
    width: 100%;
    &.login {
      background-color: var(--color-dull-green);
    }
    @media (min-width: 1200px) {
      padding: 12px 70px;
    }
    > div {
      display: flex;
      align-items: center;
    }
  }

  .flex-fill {
    flex: 1 1 auto;
    justify-content: center;
  }

  .header-logo {
    display: flex;
    justify-content: center;
    :hover {
      cursor: pointer;
    }
  }
  .header-search_box {
    align-self: center;
  }

  .header-buttons {
    display: flex;
    width: auto;
    gap: 16px;
    @media (max-width: ${BREAKPOINT_SP}) {
      width: calc(100vw - 24px);
    }
    > div {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
      justify-content: space-around;
    }
    @media (max-width: ${BREAKPOINT_SP}) {
      height: max-content;
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      gap: 8px;
    }
  }
  .header-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    svg {
      width: 24px;
      height: 24px;
    }
    .header-button-color {
      color: var(--gray-600-font-subcolor, #52525b);
      @media (max-width: ${BREAKPOINT_SP}) {
        display: -webkit-box;
        max-width: 16vw;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  .header-menu {
    cursor: pointer;
    justify-content: center;
  }

  .header-user_info {
    height: 100%;
    gap: 6px;

    .header-user-image {
      width: 36px;
      height: 36px;
      border-radius: 100%;
      overflow: hidden;
    }
    .cursorPointer {
      cursor: pointer;
    }
    .icon {
      display: flex;
      flex-direction: row;
      align-items: center;
      svg {
        color: var(--base-black, #18181b);
      }
      &:hover {
        color: var(--node-todo, #71717a);
        text-decoration: underline;
        svg {
          color: var(--node-todo, #71717a);
        }
      }
    }
  }

  .header-user_info.sp {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px;
    gap: 12px;
  }
  .header-user-coupon {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .user-coupon-icon-number {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .header-user-name-pc {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: bottom;
  }

  .header-user-name {
    display: flex;
  }

  .header-user-name-length {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-suffix {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .input-height {
    height: 100%;
    width: 100%;
    * {
      height: 100%;
    }
  }

  .input-height input {
    width: calc(100% - 28px); /* アイコンのサイズを引いた値で設定 */
    padding-right: 28px; /* アイコンの幅分だけ右側にパディングを追加 */
  }

  .fontsize-radio-container {
    padding: 12px;
  }

  .fontsize-radio {
    margin-top: 8px;
    display: flex;
    gap: 16px;
    > * {
      border: solid 1px var(--gray-400-border, #a1a1aa);
      padding: 8px;
      border-radius: 4px;
      @media (max-width: ${BREAKPOINT_SP}) {
        display: flex;
        flex-direction: row;
        gap: 24px;
      }
      .fontsize-radio-option.active {
        background-color: var(--red-100-selected-bg, #fee2e2);
        border: none;
      }
    }
  }
`;

const PullDownContainer = styled.div`
  background-color: var(--gray-300-border);
  display: flex;
  flex-direction: column;
  gap: 1px;
  .row-container {
    background-color: white;
    :hover {
      background-color: var(--background-tertiary);
    }
  }
  .menu-single-row {
    height: auto;
    min-width: 256px;
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 8px 12px;
    gap: 12px;
    cursor: pointer;
  }
  .menu-elm-icon {
    padding: 4px;
    border-radius: 4px;
    pointer-events: none;
    @media (max-width: ${BREAKPOINT_SP}) {
      padding: 4px 6px;
    }
  }
  .logout {
    background-color: var(--background-primary);
    position: fixed;
    bottom: 80px;
    right: 10px;
    display: flex;
  }
  div {
    span {
      a {
        color: inherit;
        text-decoration: none;
      }
    }
  }
`;
